package dev.kuml.portal.web.editor

import kotlin.uuid.Uuid

/**
 * Pure validation for a diagram id before it is used to build the collaboration WebSocket URL
 * (see `CollabClient.kt`'s KDoc) -- kept separate from [CollabClient] itself, matching this
 * module's established "impure glue + pure decision" convention (see that class's own KDoc:
 * "Every DECISION this class drives lives in the pure, DOM-free files next to it"), so it is
 * directly `jsNodeTest`-covered rather than needing a real WebSocket to exercise.
 *
 * R-22: re-keyed from a project id to a diagram id -- `/api/projects/{id}/collab` became
 * `/api/diagrams/{diagramId}/collab`, since a project now holds a collection of diagrams and a
 * live collaboration room is scoped to exactly one of them.
 *
 * ## Why this exists (minor security-review finding, 2026-07-27)
 * `Router.kt`'s `Screen.Editor.diagramId` comes straight from an unsanitized route path segment --
 * there is no guarantee at that point that it is a well-formed UUID (a hand-edited/bookmarked URL,
 * or a future routing bug, could hand [CollabClient] an arbitrary string). [CollabClient] used to
 * string-concatenate that value directly into a `ws://`/`wss://` URL with no format validation or
 * percent-encoding at all.
 *
 * Not exploitable end-to-end today: the scheme and host half of the URL are always the page's own
 * same-origin values ([kotlinx.browser.window.location]), never influenced by the diagram id
 * itself, so a hostile value cannot redirect the connection cross-origin; and the server's own
 * `diagramIdOrNull()` (`AiRoutes.kt`/`CollabRoutes.kt`) already rejects a non-UUID id with a plain
 * `404`. But relying ENTIRELY on that server-side check for a client that itself builds a URL out
 * of unsanitized input is a defense-in-depth gap -- a future server-side relaxation, a
 * differently-behaved intermediary/proxy in front of it, or simply a future caller of
 * [CollabClient] that is less careful about where its `diagramId` comes from, would have nothing
 * left on the CLIENT side to catch a malformed value before it is woven into a URL.
 *
 * [isValidDiagramId] enforces the exact same shape the server accepts -- [Uuid.parse] itself,
 * not a hand-rolled regex that could silently drift from the real grammar -- BEFORE
 * [CollabClient]'s own URL-building even runs. A value that already parses as a [Uuid] contains no
 * character that needs percent-encoding in a URL path segment either (only `[0-9a-fA-F-]`), so
 * validating the shape makes percent-encoding unnecessary rather than a second, orthogonal fix.
 */
public fun isValidDiagramId(diagramId: String): Boolean = runCatching { Uuid.parse(diagramId) }.isSuccess
